import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../styles/SignIn.css";
import EyesIcon from "../../icons/EyesIcon";
import UserNameIcon from "../../icons/UserNameIcon";
import LoockIcon from "../../icons/LoockIcon";
import FacebookIcon from "../../icons/facebookIcon";
import GoogleIcon from "../../icons/GoogleIcon";
import AppleIcon from "../../icons/logoApple";
import LogoSignIn from "../../icons/LogoSignIn";
import userController from "../../controllers/userController";
import { userService } from "../../services/userService";
import { getPortalHomePath } from "../../vendorFlow/access";
import Utils from "../../utils/Utils";
import vendorStorefrontController from "../../controllers/vendorStorefrontController";
import AddressPanel from "../../vendorFlow/accountSettings/AddressPanel";
import { translateLabel, translateFieldLabel, useTranslationSnapshot } from "./../../translations/runtime";


const getSignInErrorMessage = (error) => {
  const status = error?.response?.status;

  if ([400, 401, 403, 404, 417, 422].includes(status)) {
    return "Incorrect username or password.";
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Unable to sign in. Please try again."
  );
};

function SignInForm({ onSuccess }) {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const [Address, setAddress] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    const remembered = userService.getRememberedSignIn?.();

    if (remembered?.username) {
      setUsername(remembered.username);
      setPassword(remembered.password || "");
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await userController.authenticate({
        authenticateRequest: {
          password,
          username: username.trim(),
        },
      });

      if (
        response instanceof Error ||
        response?.isAxiosError ||
        response?.response
      ) {
        throw response;
      }
      const jwt = userService.getTokenFromResponse(response);

      if (!jwt) {
        throw new Error("Authentication token was not returned.");
      }

      const parsedJWT = Utils.parseJwt(jwt);
      const companyId = Object.keys(parsedJWT.userDetails || {})[0] || "";
      const businessProfileID =
        parsedJWT.userDetails?.[companyId]?.busienssProfileID || "";
      userService.saveJwt(jwt);
      userService.saveUserData({
        UserID: parsedJWT.id || "",
        Compid: companyId,
        BPID: businessProfileID,
      });

      const res = await userController.getUser({
        jwt: userService.getJwt(),
        userID: userService.getUserData().UserID,
      });

      if (
        res.data.userType === "END_USER" ||
        res.data.userType === "INDIVIDUAL_DRIVER"
      ) {
        toast.error(translateLabel("txt_common_value1_can_t_login", "{value1} can't login", {value1: res.data.userType}));
        return;
      } else {
        let businessType = "";

        try {
          const getBusinessType = await vendorStorefrontController.getSettings({
            Authorization: userService.getJwt(),
            companyId,
          });
          console.log("ascdcs", getBusinessType.data.verticalType);
          businessType = getBusinessType.data.verticalType || "";
        } catch (error) {
          console.log(error);
        }

        userService.saveUserData({
          BType: businessType,
        });
      }

      if (remember) {
        userService.setRememberedSignIn({
          username: username.trim(),
          password,
        });
      } else {
        userService.clearRememberedSignIn();
      }
      toast.success(translateLabel("txt_common_signed_in_successfully", "Signed in successfully."));
      if (onSuccess) {
        onSuccess({
          jwt: userService.getJwt(),
          payload: userService.getUserPayload(),
        });
      } else {
        if (res.data.address.length === 0) {
          setAddress(true);
        } else {
          navigate(getPortalHomePath(userService.getUserPayload()));
        }
      }
    } catch (requestError) {
      toast.error(getSignInErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {Address ? (
        <div className="sign-in-card">
          <AddressPanel mode="Sign-in" />
        </div>
      ) : (
        <div className="sign-in-card">
          <div className="sign-in-illustration">
            <LogoSignIn />
          </div>

          <div className="sign-in-title">{translateLabel("txt_sign_in", "Sign In")}</div>

          <form className="sign-in-form" onSubmit={handleSubmit}>
            {/* Username */}
            <div className="sign-in-field">
              <div className="sign-in-label">
                {translateFieldLabel("txt_end_user_username", "Username")}<span>*</span>
              </div>
              <div className="sign-in-input-wrap">
                <span className="sign-in-input-icon">
                  <UserNameIcon />
                </span>
                <input
                  className="sign-in-input"
                  type="text"
                  placeholder=""
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="sign-in-field">
              <div className="sign-in-label"> {translateFieldLabel("input_password", "Password")}<span>*</span>
              </div>
              <div className="sign-in-input-wrap">
                <span className="sign-in-input-icon">
                  <LoockIcon />
                </span>
                <input
                  className="sign-in-input"
                  type={showPassword ? "text" : "password"}
                  placeholder=""
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <span
                  className="sign-in-eye"
                  onClick={() => setShowPassword((p) => !p)}
                >
                  <EyesIcon />
                </span>
              </div>
            </div>

            {/* Remember / Forget */}
            <div className="sign-in-options">
              <label className="sign-in-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => {
                    const nextRemember = e.target.checked;
                    setRemember(nextRemember);
                    if (!nextRemember) userService.clearRememberedSignIn();
                  }}
                />
                {translateLabel("txt_end_user_remember_password", "Remember password")}
              </label>
              <span className="sign-in-forget">{translateLabel("txt_end_user_forgot_password", "Forget Password?")}</span>
            </div>

            {/* Submit */}
            <button
              className="sign-in-btn"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? translateLabel("txt_end_user_signing_in", "Signing In...") : translateLabel("txt_sign_in", "Sign In")}
            </button>
          </form>

          <div className="sign-in-signup"> {translateLabel("txt_no_account", "Don't have an account?")} <Link to="/register">{translateLabel("lnk_sign_up", "Sign Up")}</Link>
          </div>

          {/* Social */}
          {/* <div className="sign-in-social">
        <span className="sign-in-social-label">Sign in with</span>
        <div className="sign-in-social-icons">
          <span className="sign-in-social-icon">
            <AppleIcon />
          </span>
          <span className="sign-in-social-icon">
            <FacebookIcon />
          </span>
          <span className="sign-in-social-icon">
            <GoogleIcon />
          </span>
        </div>
      </div> */}

          {/* Terms */}
          <div className="sign-in-terms"> {translateLabel("txt_account_agreement", "By creating an account, you agree to D2i2a w Noss")} <br />
            <a href="/privacy">{translateLabel("lnk_privacy_policy", "Privacy Policy")}</a>.
          </div>
        </div>
      )}
    </>
  );
}

export default SignInForm;
